import React from 'react';
import {useIntl} from 'react-intl';
import classNames from 'classnames';
import {SVGS} from '../../../../../assets/images';
import TotalV2 from '../../../../../components/Schedule/total-v2';
import TimeSheetGroupTool from '../../../../../components/Schedule/timesheet-group-tool';
import TimesheetRowPreview, {TimesheetRowPreviewProps} from './timesheet-row-preview';
import styles from './timesheet-project-mobile-item.module.scss';

export type TimesheetProjectMobileItemAction = {
    id: string;
    icon: React.ReactNode;
    label: string;
    onClick?: () => void | Promise<void>;
    disabled?: boolean;
    render?: React.ReactNode;
    active?: boolean;
    variant?: 'approve' | 'billable';
};

export type TimesheetProjectMobileItemRow = TimesheetRowPreviewProps & { id: string };

type Props = {
    projectName: string;
    totals: React.ComponentProps<typeof TotalV2>;
    rows: TimesheetProjectMobileItemRow[];
    actions?: TimesheetProjectMobileItemAction[];
    defaultExpanded?: boolean;
    expanded?: boolean;
    onExpandedChange?: (expanded: boolean) => void;
    className?: string;
    showPlannedUnderTotal?: boolean;
    renderContent?: boolean;
    groupKey?: string;
    rootRef?: (node: HTMLDivElement | null) => void;
    headerRef?: (node: HTMLDivElement | null) => void;
};

const TimesheetProjectMobileItem: React.FC<Props> = ({
    projectName,
    totals,
    rows,
    actions = [],
    defaultExpanded = false,
    expanded: expandedProp,
    onExpandedChange,
    className,
    showPlannedUnderTotal = false,
    renderContent = true,
    groupKey,
    rootRef,
    headerRef
}) => {
    const intl = useIntl();
    const [expanded, setExpanded] = React.useState(defaultExpanded);
    const isExpandedControlled = expandedProp !== undefined;
    const isExpanded = isExpandedControlled ? expandedProp : expanded;
    const contentRef = React.useRef<HTMLDivElement | null>(null);
    const [contentHeight, setContentHeight] = React.useState(0);
    const totalHours = Math.round(((totals.totalHours ?? 0) * 10)) / 10;
    const plannedHours = totals.plannedHours ?? 0;
    const showPlanned = showPlannedUnderTotal && totals.plannedHours !== undefined;
    const measureContentHeight = React.useCallback(() => {
        if (!contentRef.current) {
            return;
        }

        setContentHeight(contentRef.current.scrollHeight);
    }, []);

    React.useEffect(() => {
        if (isExpandedControlled) {
            return;
        }
        setExpanded(defaultExpanded);
    }, [defaultExpanded, isExpandedControlled]);

    React.useLayoutEffect(() => {
        if (!renderContent) {
            return;
        }
        measureContentHeight();
    }, [isExpanded, measureContentHeight, renderContent, rows, totals]);

    React.useEffect(() => {
        if (!renderContent) {
            return;
        }
        const node = contentRef.current;
        if (!node) {
            return;
        }

        if (typeof ResizeObserver === 'undefined') {
            window.addEventListener('resize', measureContentHeight);
            return () => window.removeEventListener('resize', measureContentHeight);
        }

        const observer = new ResizeObserver(() => measureContentHeight());
        observer.observe(node);

        return () => observer.disconnect();
    }, [measureContentHeight, renderContent]);

    const setExpandedState = React.useCallback((next: boolean) => {
        if (!isExpandedControlled) {
            setExpanded(next);
        }
        onExpandedChange?.(next);
    }, [isExpandedControlled, onExpandedChange]);

    const toggle = React.useCallback(() => {
        setExpandedState(!isExpanded);
    }, [isExpanded, setExpandedState]);

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            toggle();
        }
    };

    const handleActionClick = (action: TimesheetProjectMobileItemAction, event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        action.onClick?.();
    };

    const contentStyle: React.CSSProperties = {
        maxHeight: isExpanded ? `${contentHeight}px` : '0px'
    };

    return (
        <div
            ref={rootRef}
            className={classNames(styles.container, className)}
            data-mobile-group-root='true'
            data-mobile-group-key={groupKey ?? ''}
            data-mobile-group-open={isExpanded}
        >
            <div
                ref={headerRef}
                className={classNames(styles.header, { [styles.headerExpanded]: isExpanded })}
                role='button'
                tabIndex={0}
                aria-expanded={isExpanded}
                onClick={toggle}
                onKeyDown={handleKeyDown}
                data-mobile-group-header='true'
                data-mobile-group-key={groupKey ?? ''}
            >
                <div className={styles.headerLeft}>
                    <SVGS.KeyboardArrowRightIcon
                        className={classNames(styles.chevron, { [styles.chevronExpanded]: isExpanded })}
                    />
                    <span className={styles.projectName} title={projectName}>{projectName}</span>
                </div>
                {actions.length > 0 && (
                    <div className={styles.actions}>
                        {actions.map((action) => {
                            const actionVariant = action.variant
                                ?? (action.id === 'approve'
                                    ? 'approve'
                                    : action.id === 'billable'
                                        ? 'billable'
                                        : undefined);
                            const isApprove = actionVariant === 'approve';
                            const isBillable = actionVariant === 'billable';
                            const showApproveHighlight = isApprove && action.active;
                            const showBillableHighlight = isBillable && action.active;
                            const dataState = showApproveHighlight
                                ? 'approve-active'
                                : showBillableHighlight
                                    ? 'billable-active'
                                    : undefined;

                            return (
                                <div key={action.id} className={styles.actionCell}>
                                    {action.render ? (
                                        <div
                                            className={styles.actionRender}
                                            onClick={(event) => event.stopPropagation()}
                                            onKeyDown={(event) => event.stopPropagation()}
                                            role='presentation'
                                        >
                                            {action.render}
                                        </div>
                                    ) : (
                                        <button
                                            type='button'
                                            className={classNames(styles.actionButton, {
                                                [styles.actionButtonActive]: action.active,
                                                [styles.actionButtonApprove]: showApproveHighlight,
                                                [styles.actionButtonBillable]: showBillableHighlight
                                            })}
                                            data-state={dataState}
                                            aria-label={action.label}
                                            title={action.label}
                                            onClick={(event) => handleActionClick(action, event)}
                                            disabled={action.disabled}
                                        >
                                            <span className={styles.actionIcon}>{action.icon}</span>
                                        </button>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {renderContent && (
                <div
                    className={classNames(styles.contentWrapper, { [styles.contentExpanded]: isExpanded })}
                    style={contentStyle}
                    aria-hidden={!isExpanded}
                >
                    <div
                        ref={contentRef}
                        className={classNames(styles.content, { [styles.contentVisible]: isExpanded })}
                    >
                        <div className={styles.totalWrapper}>
                            <div className={styles.totalSummary}>
                                <TotalV2 {...totals} />
                            </div>
                            <div className={styles.totalHours}>
                                <TimeSheetGroupTool
                                    intl={intl}
                                    totalHours={totalHours}
                                    approved={0}
                                    billed={0}
                                    approvable={0}
                                    billable={totals.billable ?? 0}
                                    hideButtons
                                    plannedHours={showPlanned ? plannedHours : undefined}
                                    hideHourSuffix
                                />
                            </div>
                        </div>
                        <div className={styles.rows}>
                            {rows.map((row) => {
                                const { id, className: rowClassName, ...rowProps } = row;
                                if(row.name === null || row.name === undefined || row.value === 0) {
                                    return null;
                                }
                                return (
                                    <TimesheetRowPreview
                                        key={id}
                                        {...rowProps}
                                        className={classNames(styles.rowCard, rowClassName)}
                                    />
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TimesheetProjectMobileItem;
